export interface BreadcrumbItem {
  label: string;
  path: string;
  key: string;
}

export interface BreadcrumbOptions {
  runName?: string;
  /** Current admin tab when path is /admin (e.g. 'users' | 'global-settings' | 'pricing-settings') */
  adminTab?: string;
}

export function getHeaderBreadcrumbs(path: string, options?: BreadcrumbOptions): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [];
  breadcrumbs.push({ label: 'Home', path: '/dashboard', key: 'home' });

  if (path === '/dashboard') {
    return breadcrumbs;
  }
  if (path === '/campaigns') {
    breadcrumbs.push({ label: 'Campaigns', path: '/campaigns', key: 'campaigns' });
  } else if (path === '/campaigns/new') {
    breadcrumbs.push({ label: 'Campaigns', path: '/campaigns', key: 'campaigns' });
    breadcrumbs.push({ label: 'New Campaign', path: '/campaigns/new', key: 'campaigns-new' });
  } else if (path === '/create-agent' || path === '/agents') {
    // LT-411: legacy /create-agent redirects to /agents.
    breadcrumbs.push({ label: 'Agents', path: '/agents', key: 'agents' });
  } else if (path === '/analytics') {
    breadcrumbs.push({ label: 'Analytics', path: '/analytics', key: 'analytics' });
  } else if (path === '/subscription') {
    breadcrumbs.push({ label: 'Subscription', path: '/subscription', key: 'subscription' });
  } else if (path === '/credits') {
    breadcrumbs.push({ label: 'Credits', path: '/credits', key: 'credits' });
  } else if (path === '/inbound-calls') {
    breadcrumbs.push({ label: 'Inbound Calls', path: '/inbound-calls', key: 'inbound-calls' });
  } else if (path === '/inbound-configuration') {
    breadcrumbs.push({ label: 'Inbound Calls', path: '/inbound-calls', key: 'inbound-calls' });
    breadcrumbs.push({ label: 'Configuration', path: '/inbound-configuration', key: 'inbound-configuration' });
  } else if (path === '/settings') {
    breadcrumbs.push({ label: 'Settings', path: '/settings', key: 'settings' });
  } else if (path === '/leads') {
    breadcrumbs.push({ label: 'Leads', path: '/leads', key: 'leads' });
  } else if (path === '/custom-variables') {
    breadcrumbs.push({ label: 'Custom Variables', path: '/custom-variables', key: 'custom-variables' });
  } else if (path === '/follow-ups') {
    breadcrumbs.push({ label: 'Follow-ups', path: '/follow-ups', key: 'follow-ups' });
  } else if (path === '/accounts') {
    breadcrumbs.push({ label: 'Accounts', path: '/accounts', key: 'accounts' });
  } else if (path.startsWith('/accounts/')) {
    breadcrumbs.push({ label: 'Accounts', path: '/accounts', key: 'accounts' });
    breadcrumbs.push({ label: 'Account Details', path: path, key: 'account-detail' });
  } else if (path === '/contacts') {
    breadcrumbs.push({ label: 'Contacts', path: '/contacts', key: 'contacts' });
  } else if (path.startsWith('/contacts/')) {
    breadcrumbs.push({ label: 'Contacts', path: '/contacts', key: 'contacts' });
    breadcrumbs.push({ label: 'Contact Details', path: path, key: 'contact-detail' });
  } else if (path === '/contact-lists') {
    breadcrumbs.push({ label: 'Contact Lists', path: '/contact-lists', key: 'contact-lists' });
  } else if (path.startsWith('/contact-lists/')) {
    breadcrumbs.push({ label: 'Contact Lists', path: '/contact-lists', key: 'contact-lists' });
    breadcrumbs.push({ label: 'List Details', path: path, key: 'contact-list-detail' });
  } else if (path === '/deals') {
    breadcrumbs.push({ label: 'Deals', path: '/deals', key: 'deals' });
  } else if (path.startsWith('/opportunities/') || path.startsWith('/deals/')) {
    breadcrumbs.push({ label: 'Deals', path: '/deals', key: 'deals' });
    breadcrumbs.push({ label: 'Deal Details', path: path, key: 'deal-detail' });
  } else if (path === '/calls') {
    breadcrumbs.push({ label: 'Calls', path: '/calls', key: 'calls' });
  } else if (path.startsWith('/calls/')) {
    breadcrumbs.push({ label: 'Calls', path: '/calls', key: 'calls' });
    breadcrumbs.push({ label: 'Call Details', path: path, key: 'call-detail' });
  } else if (path === '/sequences') {
    breadcrumbs.push({ label: 'Sequences', path: '/sequences', key: 'sequences' });
  } else if (path.startsWith('/sequences/')) {
    breadcrumbs.push({ label: 'Sequences', path: '/sequences', key: 'sequences' });
    if (path.endsWith('/edit') || path === '/sequences/new/edit') {
      breadcrumbs.push({ label: 'Edit', path: path, key: 'sequence-edit' });
    } else {
      breadcrumbs.push({ label: 'Sequence Details', path: path, key: 'sequence-detail' });
    }
  } else if (path.startsWith('/agents/')) {
    breadcrumbs.push({ label: 'Agents', path: '/agents', key: 'agents' });
    breadcrumbs.push({ label: 'Agent Details', path: path, key: 'agent-detail' });
  } else if (path.startsWith('/campaigns/')) {
    breadcrumbs.push({ label: 'Campaigns', path: '/campaigns', key: 'campaigns' });
    breadcrumbs.push({ label: 'Campaign', path: path, key: 'campaign-detail' });
  } else if (path === '/sms-templates') {
    breadcrumbs.push({ label: 'SMS Templates', path: '/sms-templates', key: 'sms-templates' });
  } else if (path === '/email-templates') {
    breadcrumbs.push({ label: 'Email Templates', path: '/email-templates', key: 'email-templates' });
  } else if (path === '/feedback') {
    breadcrumbs.push({ label: 'Feedback', path: '/feedback', key: 'feedback' });
  } else if (path === '/support') {
    breadcrumbs.push({ label: 'Support', path: '/support', key: 'support' });
  } else if (path === '/admin') {
    breadcrumbs.push({ label: 'Admin Panel', path: '/admin', key: 'admin' });
    const tab = options?.adminTab || 'users';
    if (tab === 'global-settings') {
      breadcrumbs.push({ label: 'Global Settings', path: '/admin?tab=global-settings', key: 'admin-global-settings' });
    } else if (tab === 'pricing-settings') {
      breadcrumbs.push({ label: 'Pricing Settings', path: '/admin?tab=pricing-settings', key: 'admin-pricing-settings' });
    } else if (tab === 'privacy-terms') {
      breadcrumbs.push({ label: 'Privacy / Terms & Conditions', path: '/admin?tab=privacy-terms', key: 'admin-privacy-terms' });
    } else {
      breadcrumbs.push({ label: 'Users', path: '/admin', key: 'admin-users' });
    }
  } else if (path === '/admin/global-settings') {
    breadcrumbs.push({ label: 'Admin Panel', path: '/admin', key: 'admin' });
    breadcrumbs.push({ label: 'Global Settings', path: '/admin?tab=global-settings', key: 'admin-global-settings' });
  } else if (path === '/admin/pricing-settings') {
    breadcrumbs.push({ label: 'Admin Panel', path: '/admin', key: 'admin' });
    breadcrumbs.push({ label: 'Pricing Settings', path: '/admin?tab=pricing-settings', key: 'admin-pricing-settings' });
  } else if (path === '/admin/feedbacks') {
    breadcrumbs.push({ label: 'Admin Panel', path: '/admin', key: 'admin' });
    breadcrumbs.push({ label: 'Feedbacks', path: '/admin/feedbacks', key: 'admin-feedbacks' });
  } else if (path === '/admin/tickets') {
    breadcrumbs.push({ label: 'Admin Panel', path: '/admin', key: 'admin' });
    breadcrumbs.push({ label: 'Support Tickets', path: '/admin/tickets', key: 'admin-tickets' });
  } else if (path.startsWith('/admin/users/')) {
    breadcrumbs.push({ label: 'Admin Panel', path: '/admin', key: 'admin' });
    breadcrumbs.push({ label: 'User Details', path: path, key: 'admin-user-detail' });
  } else if (path.startsWith('/leads/') && path !== '/leads') {
    breadcrumbs.push({ label: 'Leads', path: '/leads', key: 'leads' });
    breadcrumbs.push({ label: 'Lead Details', path: path, key: 'lead-detail' });
  } else if (path.startsWith('/runs/')) {
    const segments = path.split('/');
    if (segments.length >= 3) {
      const runId = segments[2];
      const runLabel = options?.runName ?? `Campaign ${runId.slice(0, 8)}`;
      breadcrumbs.push({ label: 'Campaigns', path: '/campaigns', key: 'campaigns-parent' });
      if (segments.length >= 5 && segments[3] === 'calls') {
        breadcrumbs.push({ label: runLabel, path: `/runs/${runId}`, key: `run-${runId}` });
        breadcrumbs.push({ label: 'Call Details', path: path, key: 'call-detail' });
      } else {
        breadcrumbs.push({ label: runLabel, path: `/runs/${runId}`, key: `run-${runId}` });
      }
    }
  }
  return breadcrumbs;
}
